import { TabContext } from "@mui/lab";
import { Divider, Grid2, Tab } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CustomDiv } from "../../../../modules/chat-bot/style";
import {
  CommonGrid2,
  TabPanelView,
} from "../../../../modules/common-components/StyledComponents";
import { CustomizeSpan } from "../../../../modules/jobs/style";
import {
  allDataConnector,
  connectDataConnector,
  disconnectDataConnector,
} from "../../../../redux/action/OrganizationSetup";
import { ShowSnackbar } from "../../../../redux/action/ShowSnackbar";
import { SubscriptionTabList } from "../../style";
import Card from "./component/Card";
import CredentialsDialog from "./component/CredentialsDialog";

export default function DataConnector() {
  const [value, setValue] = useState(0);
  const [dataConnector, setDataConnector] = useState({
    data: ["", ""],
    loading: false,
  });
  const dispatch = useDispatch();

  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState({
    open: false,
    loading: false,
  });
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [credentialValues, setCredentialValues] = useState({});

  const tabData = [
    { name: "All", count: dataConnector?.data?.length },
    {
      name: "Connected",
      count: dataConnector?.data?.filter((item) => item.connected)?.length,
    },
  ];

  const handleTabChange = (event, newValue) => setValue(newValue);

  const handleOpenConnectDialog = (connector) => {
    setSelectedConnector(connector);
    const initialValues = {};
    (connector?.required_fields || []).forEach((field) => {
      const existingValue = connector?.current_credentials?.[field.key];
      initialValues[field.key] = existingValue ?? "";
    });
        if (connector?.name === "OpenAI") {
      initialValues["global_model"] = connector?.current_credentials?.global_model ?? "";
    }
    setCredentialValues(initialValues);
    setCredentialsDialogOpen((prev) => ({
      ...prev,
      open: true,
    }));
  };

  const handleDisconnect = (connector) => {
    const orgId = JSON.parse(
      localStorage.getItem("credential") || "{}"
    )?.organization_id;
    (async () => {
      try {
        setDataConnector((prev) => ({ ...prev, loading: true }));
        const payload = {
          organization_id: orgId,
          data_connector_id: connector.id,
        };
        await disconnectDataConnector(payload);
        setDataConnector((prev) => ({
          ...prev,
          loading: false,
          data: (prev.data || []).map((item) =>
            item.id === connector.id ? { ...item, connected: false } : item
          ),
        }));
        dispatch(
          ShowSnackbar({
            open: true,
            message: `${connector.name} disconnected`,
            severity: "success",
          })
        );
        functionCall();
      } catch (error) {
        dispatch(
          ShowSnackbar({
            open: true,
            message: error?.message || "Failed to disconnect",
            severity: "error",
          })
        );
        setDataConnector((prev) => ({ ...prev, loading: false }));
      }
    })();
  };

  const handleCloseCredentialsDialog = () => {
    setCredentialsDialogOpen({ open: false, loading: false });
    setSelectedConnector(null);
    setCredentialValues({});
  };

  const handleCredentialChange = (key, val) => {
    setCredentialValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmitCredentials = async () => {
    const orgId = JSON.parse(
      localStorage.getItem("credential") || "{}"
    )?.organization_id;
    try {
      setCredentialsDialogOpen((prev) => ({
        ...prev,
        loading: true,
      }));
      setDataConnector((prev) => ({ ...prev, loading: true }));
      const resolvedCredentials = Object.fromEntries(
      Object.entries(credentialValues).map(([key, val]) => [
        key,
        val && typeof val === "object" && "value" in val ? val.value : val,
      ])
    );
      const payload = {
        organization_id: orgId,
        data_connector_id: selectedConnector.id,
        
        ...resolvedCredentials,
      };
      let data = await connectDataConnector(payload);
      if (selectedConnector.name === "calendly") {
        window.open(
          data?.data?.data?.calendly_authorization_url,
          "_blank",
          "noopener,noreferrer"
        );
      }

      setDataConnector((prev) => ({
        ...prev,
        loading: false,
        data: (prev.data || []).map((item) =>
          item.id === selectedConnector.id
            ? {
                ...item,
                connected: true,
                current_credentials: {
                  ...item.current_credentials,
                  ...credentialValues,
                },
              }
            : item
        ),
      }));
      dispatch(
        ShowSnackbar({
          open: true,
          message: `${selectedConnector.name} connected`,
          severity: "success",
        })
      );

      functionCall();
      handleCloseCredentialsDialog();
    } catch (error) {
      setCredentialsDialogOpen((prev) => ({
        ...prev,
        loading: false,
      }));
      dispatch(
        ShowSnackbar({
          open: true,
          message: error?.message || "Failed to connect",
          severity: "error",
        })
      );
      setDataConnector((prev) => ({ ...prev, loading: false }));
    }
  };

  const functionCall = async () => {
    setDataConnector((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const orgId = JSON.parse(
        localStorage.getItem("credential") || "{}"
      )?.organization_id;
      const response = await allDataConnector(`organization_id=${orgId}`);
      const rawList = response?.data?.data || response?.data || [];
      const list = Array.isArray(rawList) ? rawList : [];
      const ordered = [...list].sort((a, b) => {
        const order = { OpenAI: 0, TechFetch: 1 };
        const aRank = order[a?.name] ?? 1;
        const bRank = order[b?.name] ?? 1;
        if (aRank !== bRank) return aRank - bRank;
        return (a?.name || "").localeCompare(b?.name || "");
      });
      setDataConnector((prev) => ({
        ...prev,
        data: ordered,
        loading: false,
      }));
    } catch (error) {
      setDataConnector((prev) => ({
        ...prev,
        loading: false,
      }));
      dispatch(
        ShowSnackbar({
          open: true,
          message: error?.message || "Failed to load data connectors",
          severity: "error",
        })
      );

      setDataConnector((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  useEffect(() => {
    functionCall();
  }, []);

  return (
    <CustomDiv padding={"0rem 1.5rem 1.5rem"}>
      <TabContext value={value}>
        <Grid2 alignItems="center" marginTop={3}>
          <SubscriptionTabList
            onChange={handleTabChange}
            aria-label="create-job"
          >
            {tabData.map((item, index) => (
              <Tab
                key={index}
                label={
                  <CommonGrid2 display={"flex"} gap={"10px"}>
                    {item.name}
                    <CustomizeSpan color="#5B738B">
                      ({item.count})
                    </CustomizeSpan>
                  </CommonGrid2>
                }
                value={index}
              />
            ))}
          </SubscriptionTabList>
        </Grid2>

        <Divider />
        <TabPanelView value={0} padding="2rem 0px 0px">
          <Card
            dataConnector={dataConnector.data || []}
            loading={dataConnector.loading}
            onConnect={handleOpenConnectDialog}
            onDisconnect={handleDisconnect}
          />
        </TabPanelView>
        <TabPanelView value={1} padding="1.5rem 0px 0px">
          <Card
            dataConnector={
              (dataConnector.data &&
                dataConnector.data.filter((item) => item.connected)) ||
              []
            }
            loading={dataConnector.loading}
            onConnect={handleOpenConnectDialog}
            onDisconnect={handleDisconnect}
          />
        </TabPanelView>
      </TabContext>

      <CredentialsDialog
        open={credentialsDialogOpen}
        onClose={handleCloseCredentialsDialog}
        connector={selectedConnector}
        values={credentialValues}
        onChange={handleCredentialChange}
        onSubmit={handleSubmitCredentials}
      />
    </CustomDiv>
  );
}
